import { z } from "zod/v4"
import { type DateValue } from "@internationalized/date"

import { CoordinateEmpty, CoordinateSchema } from "$lib/data/schema/coordinate"
import { RelationEmpty, RelationSchema } from "$lib/data/schema/_relation"

export type { Coordinate } from "$lib/data/schema/coordinate"
export type { Relation } from "$lib/data/schema/_relation"

const CustomerSchema = z.object({
  id: z.number().default(0),
  name: z.string().default(""),
  number: z.string().default(""),
  address: z.string().default(""),
  latitude: z.number().default(0),
  longitude: z.number().default(0),
  radius: z.number().default(0),
})
export type Customer = z.infer<typeof CustomerSchema>

export const DocumentationSchema = z.object({
  id: z.number().default(0),
  ref_id: z.string().default(""),
  name: z.string().default(""),
  description: z.string().default(""),
  document: z.union([z.file(), z.null()]).default(null),
  document_url: z.string().default(""),
  document_delete: z.number().default(0),
})
export type Documentation = z.infer<typeof DocumentationSchema>
export const DocumentationEmpty = DocumentationSchema.parse({})

export const DocumentationAssetSchema = z.object({
  id: z.number().default(0),
  ref_id: z.string().default(""),
  asset: RelationSchema.default(RelationEmpty),
  asset_id: z.number().default(0),
  name: z.string().default(""),
  description: z.string().default(""),
  status: z.boolean().default(false),
  document: z.union([z.file(), z.null()]).default(null),
  document_url: z.string().default(""),
  document_delete: z.number().default(0),
})
export type DocumentationAsset = z.infer<typeof DocumentationAssetSchema>
export const DocumentationAssetEmpty = DocumentationAssetSchema.parse({})

export const ActivitySchema = z.object({
  id: z.number().default(0),
  activity: RelationSchema.default(RelationEmpty),
  number: z.string().default(""),
  date: z.string().default(""),
  is_plan: z.boolean().default(false),
  category: RelationSchema.default(RelationEmpty),
  customer_number: z.string().default(""),
  customer_type: RelationSchema.default(RelationEmpty),
  customer: CustomerSchema.default(CustomerSchema.parse({})),
  is_other: z.boolean().default(false),
  name: z.string().default(""),
  description: z.string().default(""),
  at_start: z.string().default(""),
  at_end: z.string().default(""),
  stage: RelationSchema.default(RelationEmpty),
  status: RelationSchema.default(RelationEmpty),
  plan_approval_at: z.string().default(""),
  plan_approval_status: RelationSchema.default(RelationEmpty),
  plan_approval_by: RelationSchema.default(RelationEmpty),
  plan_approval_description: z.string().default(""),
  real_status: RelationSchema.default(RelationEmpty),
  real_valid: z.boolean().default(false),
  real_start_datetime: z.string().default(""),
  real_start_coordinate: CoordinateSchema.default(CoordinateEmpty),
  real_end_datetime: z.string().default(""),
  real_end_coordinate: CoordinateSchema.default(CoordinateEmpty),
  real_description: z.string().default(""),
  real_response: RelationSchema.default(RelationEmpty),
  real_problem_is: z.boolean().default(false),
  real_problem: RelationSchema.default(RelationEmpty),
  real_problem_description: z.string().default(""),
  real_next_is: z.boolean().default(false),
  real_next: RelationSchema.default(RelationEmpty),
  real_next_date: z.string().default(""),
  documentation_assets: z.array(DocumentationAssetSchema).default([]),
  documentation_others: z.array(DocumentationSchema).default([]),
  documentations: z.array(DocumentationSchema).default([]),
  approval_at: z.string().default(""),
  approval_status: RelationSchema.default(RelationEmpty),
  approval_by: RelationSchema.default(RelationEmpty),
  approval_description: z.string().default(""),
})
export type Activity = z.infer<typeof ActivitySchema>
export const ActivityEmpty = ActivitySchema.parse({})

export const RealizationFormSchema = z.object({
  id: z.number().default(0),
  activity_id: z.number().default(0),
  activity_name: z.string().default(""),
  number: z.string().default(""),
  date: z.string().default(""),
  is_plan: z.boolean().default(false),
  category_name: z.string().default(""),
  customer_type_name: z.string().default(""),
  customer_number: z.string().default(""),
  customer_name: z.string().default(""),
  is_other: z.number().default(0),
  name: z.string().default(""),
  description: z.string().default(""),
  at_start: z.string().default(""),
  at_end: z.string().default(""),
  stage_name: z.string().default(""),
  status_name: z.string().default(""),
  real_status_id: z.number().default(0),
  real_status_name: z.string().default(""),
  real_start: z.boolean().default(false),
  real_start_datetime: z.string().default(""),
  real_end: z.boolean().default(false),
  real_end_datetime: z.string().default(""),
  real_present: z.boolean().default(false),
  real_description: z.string().default(""),
  real_response_id: z.number().default(0),
  real_response_name: z.string().default(""),
  real_problem_is: z.number().default(0),
  real_problem_id: z.number().default(0),
  real_problem_name: z.string().default(""),
  real_problem_description: z.string().default(""),
  real_next_is: z.number().default(0),
  real_next_id: z.number().default(0),
  real_next_name: z.string().default(""),
  real_next_date: z.custom<DateValue | null>().default(null),
  documentation_others: z.array(DocumentationSchema).default([]),
  documentation_assets: z.array(DocumentationAssetSchema).default([]),
  documentations: z.array(DocumentationSchema).default([]),
})
export type RealizationForm = z.infer<typeof RealizationFormSchema>
export const RealizationFormEmpty = RealizationFormSchema.parse({})
